import { Link, useNavigate } from "react-router-dom";
import { Sparkline, TrendChip } from "./Sparkline";
import { getFinancialKpiTone, getAttentionSeverity, getAgingBucketColor, ATTENTION_PALETTE } from "@/utils/dashboardColors";
import { getKpiIcon, getAttentionIcon, getDashboardIconColor } from "@/utils/dashboardIcons";
import { toTitleLabel } from "@/utils/textFormat";

// Shared visual affordance for KPI cards — applied via Link wrapper.
// Preserves the underlying pigment card colors and chart colors exactly.
const KPI_LINK_CLASS = "block cursor-pointer transition transform hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-xl";

// --- Revenue ---------------------------------------------------------------
export function RevenueCard({ data, period }) {
  if (!data) return <CardSkeleton />;
  const tgtPct = Math.min(data.progressPercent || 0, 100);
  const tone = getFinancialKpiTone("revenue", data.value, data.target, data.trendPercent, { progressPercent: data.progressPercent });
  const Icon = getKpiIcon("revenue");
  return (
    <Link to="/finance?tab=invoices&sourceTile=revenue-mtd&sourceDashboard=main"
          className={KPI_LINK_CLASS} title={`Open Finance · Invoices for ${period}`} data-testid="kpi-revenue-link">
      <div className="co-card p-sage p-5 border-l-4" style={{ borderLeftColor: tone.color }} data-testid="kpi-revenue-card">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          <span className="flex items-center gap-1.5"><Icon className="w-4 h-4" style={{ color: tone.color }} />Invoice Revenue · {period}</span>
          <TrendChip value={data.trendPercent} />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="text-4xl font-bold" style={{ fontFamily: "Plus Jakarta Sans", color: "#0F172A" }}>{data.formattedValue}</div>
          <div className="text-xs text-slate-500">/ {data.formattedTarget} tgt</div>
        </div>
        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid="revenue-progress">
          <div className="h-full" style={{ width: `${tgtPct}%`, background: tone.color }} />
        </div>
        <div className="text-[11px] text-slate-500 mt-1.5">{Math.round(tgtPct)}% of target</div>
        <div className="mt-1"><Sparkline points={data.sparkline} color="#2563EB" /></div>
      </div>
    </Link>
  );
}

// --- Collections -----------------------------------------------------------
export function CollectionsCard({ data, period }) {
  if (!data) return <CardSkeleton />;
  const tone = getFinancialKpiTone("collections", data.value, null, data.trendPercent);
  const Icon = getKpiIcon("collections");
  return (
    <Link to="/finance?tab=receipts&sourceTile=collections-mtd&sourceDashboard=main"
          className={KPI_LINK_CLASS} title={`Open Finance · Receipts for ${period}`} data-testid="kpi-collections-link">
      <div className="co-card p-sky p-5 border-l-4" style={{ borderLeftColor: tone.color }} data-testid="kpi-collections-card">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          <span className="flex items-center gap-1.5"><Icon className="w-4 h-4" style={{ color: tone.color }} />Collections · {period}</span>
          <TrendChip value={data.trendPercent} />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="text-4xl font-bold" style={{ fontFamily: "Plus Jakarta Sans", color: "#0F172A" }}>{data.formattedValue}</div>
          <div className="text-xs text-slate-500">vs {data.formattedPreviousPeriodValue} prev</div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-1 text-[10px] uppercase font-bold">
          {data.breakdown?.length > 0 ? data.breakdown.slice(0, 3).map((b) => (
            <div key={b.label} className="px-2 py-1.5 bg-slate-50 rounded text-slate-600 text-center" data-testid={`collections-breakdown-${(b.label || "").toLowerCase()}`}>
              <div className="text-slate-700">{toTitleLabel(b.label)}</div>
              <div className="text-slate-500 mt-0.5 normal-case">{b.percent}%</div>
            </div>
          )) : <div className="col-span-3 text-[11px] text-slate-400 normal-case font-normal">No payments recorded.</div>}
        </div>
        <div className="mt-1"><Sparkline points={data.sparkline} color="#16A34A" /></div>
      </div>
    </Link>
  );
}

// --- Outstanding AR --------------------------------------------------------
// 2026-09-06 · Sprint DASH-CMD-CENTRE Slice 0 — keys are the CANONICAL
// backend bucket codes (`core/analytics_contracts.AGING_KEYS`), used
// verbatim for drill-through URL params AND colour lookup. Payload
// carries `bucket` (machine key) + `label` (display only). We MUST NOT
// match on `label` — labels drift, keys are contracts.
const AR_BUCKET_CODES = ["CURRENT", "D1_30", "D31_60", "D61_90", "D91_180", "D180_PLUS"];
const OVERDUE_90_PLUS_KEYS = new Set(["D91_180", "D180_PLUS"]);
export function OutstandingArCard({ data }) {
  const navigate = useNavigate();
  if (!data) return <CardSkeleton />;
  // Recompute the 90+ severity chip from BOTH 90-plus buckets (D91_180
  // + D180_PLUS) — the legacy single-label find missed half the tail.
  const nineties = (data.agingBuckets || []).filter((b) => OVERDUE_90_PLUS_KEYS.has(b.bucket));
  const overdue90Value = nineties.reduce((acc, b) => acc + (b.value || 0), 0);
  const overdue90Pct = data.value > 0 ? Math.round((overdue90Value / data.value) * 1000) / 10 : 0;
  const tone = getFinancialKpiTone("outstanding_ar", data.value, null, null, { overdue90Percent: overdue90Pct });
  const Icon = getKpiIcon("outstanding_ar");
  const tileHref = "/finance?tab=invoices&filter=outstanding_ar&sourceTile=outstanding-ar&sourceDashboard=main";
  const goToTile = () => navigate(tileHref);
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToTile(); } };
  return (
    <div role="link" tabIndex={0} onClick={goToTile} onKeyDown={onKey}
         className={KPI_LINK_CLASS} title="Open Finance · Outstanding AR (canonical)" data-testid="kpi-outstanding-link" data-href={tileHref}>
      <div className="co-card p-amber p-5 border-l-4" style={{ borderLeftColor: tone.color }} data-testid="kpi-outstanding-card">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          <span className="flex items-center gap-1.5"><Icon className="w-4 h-4" style={{ color: tone.color }} />Outstanding AR</span>
          {overdue90Value > 0 ? (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "#FEF2F2", color: "#E11D48" }}
                  data-testid="kpi-outstanding-90plus-chip">
              {overdue90Pct}% in 90+
            </span>
          ) : null}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="text-4xl font-bold" style={{ fontFamily: "Plus Jakarta Sans", color: "#0F172A" }}>{data.formattedValue}</div>
          <div className="text-xs text-slate-500">across {data.invoiceCount} invoices</div>
        </div>
        <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-slate-100" data-testid="aging-bar">
          {(data.agingBuckets || []).map((b) => (
            <div key={b.bucket || b.label}
                 style={{ width: `${Math.max(b.percent, 0)}%`, background: getAgingBucketColor(b.bucket) }}
                 title={`${b.label}: ${b.value}`} />
          ))}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1 text-[10px] font-semibold">
          {(data.agingBuckets || []).map((b) => {
            const code = AR_BUCKET_CODES.includes(b.bucket) ? b.bucket : null;
            const bucketHref = code
              ? `/finance?tab=invoices&filter=outstanding_ar&ar_bucket=${code}&sourceTile=outstanding-ar&sourceDashboard=main`
              : tileHref;
            return (
              <Link
                key={b.bucket || b.label}
                to={bucketHref}
                onClick={(e) => e.stopPropagation()}
                data-testid={`aging-${b.bucket || b.label}`}
                title={`Drill into ${b.label} · ${b.invoiceCount || 0} invoices · ${b.value}`}
                style={{ color: getAgingBucketColor(b.bucket) }}
                className="hover:underline truncate"
              >
                {b.label} · {b.percent}%
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- Gross Margin ----------------------------------------------------------
export function GrossMarginCard({ data }) {
  if (!data) return <CardSkeleton />;
  // Phase DASH-KPI-PART-C — the backend now emits a `marginDisplayState`
  // enum so we never render "100%" when there are no approved cost
  // records. The tone helper only fires when the metric is actually
  // computable (fallback to sky as a neutral tone otherwise).
  const computable = data.marginIsComputable === true;
  const state = data.marginDisplayState || (computable ? "OK" : "NO_REVENUE");
  const tone = computable
    ? getFinancialKpiTone("gross_margin", data.valuePercent, data.targetPercent, data.trendPercentagePoints)
    : { color: "#0EA5E9" };
  const Icon = getKpiIcon("gross_margin");
  const previousAvailable = data.previousPeriodAvailable === true;
  const targetPct = data.targetPercentage ?? data.targetPercent;
  const stateCopy = (
    state === "COST_DATA_MISSING" ? "Cost data missing" :
    state === "NO_REVENUE"        ? "No revenue in period" :
                                    null
  );

  return (
    <Link to="/finance?tab=invoices&sourceTile=gross-margin&sourceDashboard=main"
          className={KPI_LINK_CLASS} title="Open Finance · Margin context" data-testid="kpi-margin-link">
      <div className="co-card p-mint p-5 border-l-4" style={{ borderLeftColor: tone.color }} data-testid="kpi-margin-card" data-margin-state={state}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          <span className="flex items-center gap-1.5"><Icon className="w-4 h-4" style={{ color: tone.color }} />Gross Margin</span>
          {/* Trend chip only when previous period is independently computable. */}
          {previousAvailable && data.trendPercentagePoints != null
            ? <TrendChip value={data.trendPercentagePoints} suffix=" pp" />
            : <span className="text-[10px] font-semibold text-slate-400" data-testid="gm-no-prior-period">No prior period</span>}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          {computable ? (
            <>
              <div className="text-4xl font-bold" style={{ fontFamily: "Plus Jakarta Sans", color: "#0F172A" }} data-testid="gm-value">
                {data.valuePercent}%
              </div>
              <div className="text-xs text-slate-500" data-testid="gm-target">tgt {targetPct}%</div>
            </>
          ) : (
            <>
              <div className="text-3xl font-bold text-slate-500" style={{ fontFamily: "Plus Jakarta Sans" }} data-testid="gm-value-na">
                N/A
              </div>
              <div className="text-xs text-slate-500" data-testid="gm-state-copy">· {stateCopy}</div>
            </>
          )}
        </div>
        {/* Target delta — only when current margin is computable. */}
        {computable && data.targetDeltaPercentagePoints != null ? (
          <div className={`text-[11px] font-semibold mt-1 ${data.targetDeltaPercentagePoints >= 0 ? "text-emerald-600" : "text-rose-600"}`}
               data-testid="gm-target-delta">
            {data.targetDeltaPercentagePoints >= 0 ? "▲" : "▼"} {Math.abs(data.targetDeltaPercentagePoints)} pp vs target
          </div>
        ) : null}
        {computable ? (
          <div className="text-[11px] text-slate-500 mt-1.5">
            Top drag · {data.topDragLabel ? <span className="font-semibold text-rose-600">{data.topDragLabel}</span> : <span className="text-slate-400">no cost drag</span>}
          </div>
        ) : state === "COST_DATA_MISSING" ? (
          <div className="text-[11px] text-amber-700 mt-1.5" data-testid="gm-cost-missing-hint">
            Revenue captured ({typeof data.revenueAmount === "number" ? Math.round(data.revenueAmount).toLocaleString() : "—"}) but no APPROVED expenses / vendor bills yet.
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 mt-1.5" data-testid="gm-no-revenue-hint">
            No issued invoice revenue in this period.
          </div>
        )}
        <div className="text-[10px] text-slate-400 mt-1" data-testid="gm-cost-basis">Cost basis: APPROVED / PAID only</div>
        <div className="mt-1"><Sparkline points={data.sparkline || []} color="#0EA5E9" /></div>
      </div>
    </Link>
  );
}

// --- Attention strip --------------------------------------------------------
export function AttentionStrip({ items }) {
  if (!Array.isArray(items)) return null;
  return (
    <div className="attention-panel p-5 mb-6" data-testid="attention-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white" style={{ background: "#F59E0B" }}>!</div>
          <div>
            <div className="font-bold text-slate-900">Needs your attention</div>
            <div className="text-xs text-slate-500">Only items waiting on Owner · auto-clears when actioned</div>
          </div>
        </div>
        <button
          className="text-[11px] font-semibold text-cyan-700 px-3 py-1 rounded-full border border-cyan-200 bg-white hover:bg-cyan-50 hover:border-cyan-400 transition"
          data-testid="attention-snooze">Snooze all →</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 items-stretch">
        {items.map((c) => <AttentionCard key={c.key} card={c} />)}
      </div>
    </div>
  );
}

function AttentionCard({ card }) {
  const computed = getAttentionSeverity(card.key, card.value, { severeOverdue: card.severeOverdue });
  // If backend already says critical → escalate to red
  const sev = (card.severity === "critical" && Number(card.value) > 0) ? "red" : computed;
  const palette = ATTENTION_PALETTE[sev] || ATTENTION_PALETTE.muted;
  const Icon = getAttentionIcon(card.key);
  const iconColor = getDashboardIconColor(card.key, sev === "muted" ? "slate" : sev);
  // Semantic 4px left-border accent — scan-readable in <2s.
  // Map computed severity → KPI accent class declared in index.css.
  const accentClass = (
    sev === "red"     ? "kpi-accent-critical" :
    sev === "amber"   ? "kpi-accent-warning"  :
    sev === "warning" ? "kpi-accent-warning"  :
    sev === "muted"   ? "kpi-accent-neutral"  :
                        "kpi-accent-neutral"
  );
  // Phase 12: always append sourceTile so the destination page can show
  // a drill-down chip + parse incoming filter parameters.
  const baseUrl = card.targetUrl || "";
  const disabled = !baseUrl || baseUrl === "#";
  const joiner = baseUrl.includes("?") ? "&" : "?";
  const targetUrl = disabled ? "#" : `${baseUrl}${joiner}sourceTile=${encodeURIComponent(card.key)}`;

  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
          {card.label}
        </div>
        <span className={`w-2 h-2 rounded-full ${palette.dot}`} />
      </div>
      <div className={`text-3xl font-bold mt-1 ${palette.value}`} style={{ fontFamily: "Plus Jakarta Sans" }} data-testid={`attention-${card.key}-value`}>{card.value}</div>
      <div className="text-[11px] text-slate-500 mt-1 truncate">{card.description}</div>
      <div className="text-[11px] font-semibold text-cyan-700 mt-1">
        {disabled ? <span className="text-slate-400 italic" data-testid={`attention-${card.key}-coming-soon`}>Coming soon</span> : card.actionLabel}
      </div>
    </>
  );
  if (disabled) {
    return (
      <div className={`attention-tile ${accentClass} block p-4 opacity-70 cursor-not-allowed select-none`}
           data-testid={`attention-${card.key}`}
           data-accent={accentClass}
           aria-disabled="true" title="Destination not available yet">
        {inner}
      </div>
    );
  }
  return (
    <Link to={targetUrl}
          className={`attention-tile ${accentClass} block p-4 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400`}
          data-testid={`attention-${card.key}`}
          data-accent={accentClass}>
      {inner}
    </Link>
  );
}

function CardSkeleton() {
  return <div className="co-card p-5 h-32 animate-pulse bg-slate-50" />;
}
